/**
 * MainPage — Phase-7-Dashboard fuer Auftragsverfolgung.
 *
 * Layout:
 *   Header (Programm-Branding + Refresh-Button)
 *   FilterBar (UI-04) — controlled, MainPage haelt den FilterState
 *   Karten-Grid (sortiert order_received_at DESC via Backend, Pagination) — UI-01
 *   OrderCard pro GroupedOrder (UI-02/03/06/07)
 *   Pagination-Footer
 *   OrderDetailDrawer (on demand) — UI-05/11
 *
 * Race-Condition-Schutz: useEffect mit cancelled-Flag — RESEARCH Risiko 3.
 * Filter-Reset auf Page 1 bei jeder Filter-Aenderung.
 */

import { useCallback, useEffect, useState } from 'react';
import { PackageSearch, ChevronLeft, ChevronRight, RefreshCw, AlertCircle } from 'lucide-react';
import { api } from '@libero/core/services/api';
import FilterBar, { FilterState } from '../components/FilterBar';
import OrderCard from '../components/OrderCard';
import OrderDetailDrawer from '../components/OrderDetailDrawer';
import { groupOrders, OrderListItem } from '../lib/groupOrders';

interface ListResponse {
  orders: OrderListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const INITIAL_FILTERS: FilterState = {
  status: [],
  hasPackage: 'all',
  carrier: 'all',
  onlyStuck: false,
  search: '',
};

type VersandKategorie = 'paket' | 'spedition';

function buildQueryString(filters: FilterState, page: number, kategorie: VersandKategorie): string {
  const params = new URLSearchParams();
  if (filters.status.length > 0) params.set('status', filters.status.join(','));
  if (filters.hasPackage !== 'all') params.set('hasPackage', filters.hasPackage);
  if (filters.carrier !== 'all') params.set('carrier', filters.carrier);
  if (filters.onlyStuck) params.set('onlyStuck', 'true');
  if (filters.search.trim()) params.set('search', filters.search.trim());
  params.set('versandKategorie', kategorie);
  params.set('page', String(page));
  params.set('limit', '50');
  return params.toString();
}

export default function MainPage() {
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [kategorie, setKategorie] = useState<VersandKategorie>('paket');
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Tab-Counts (parallel geladen damit beide Tabs ihre Anzahl zeigen)
  const [counts, setCounts] = useState<{ paket: number; spedition: number }>({ paket: 0, spedition: 0 });

  // Filter-Aenderung → Page auf 1 zuruecksetzen
  const handleFilterChange = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  const handleKategorieChange = (next: VersandKategorie) => {
    setKategorie(next);
    setPage(1);
  };

  // Manuelles Re-Fetch (Refresh-Button + nach Drawer-Aktionen)
  const triggerReload = useCallback(() => setReloadKey(k => k + 1), []);

  // Daten laden bei Filter-/Page-/Kategorie-/Reload-Aenderung
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const qs = buildQueryString(filters, page, kategorie);
    api
      .get(`/programmes/auftragsverfolgung/list?${qs}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data as ListResponse;
        setOrders(data.orders ?? []);
        setTotal(data.total ?? 0);
        setTotalPages(data.totalPages ?? 0);
        setCounts((prev) => ({ ...prev, [kategorie]: data.total ?? 0 }));
      })
      .catch((err: Error & { response?: { data?: { error?: { message?: string } } } }) => {
        if (cancelled) return;
        setError(
          err.response?.data?.error?.message ?? err.message ?? 'Unbekannter Fehler',
        );
        setOrders([]);
        setTotal(0);
        setTotalPages(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, page, kategorie, reloadKey]);

  // Tab-Counter fuer den anderen (nicht-aktiven) Tab nachladen — 1 schmaler Call
  useEffect(() => {
    let cancelled = false;
    const otherKategorie: VersandKategorie = kategorie === 'paket' ? 'spedition' : 'paket';
    const qs = buildQueryString(filters, 1, otherKategorie) + '&limit=1';
    api.get(`/programmes/auftragsverfolgung/list?${qs}`).then((res) => {
      if (cancelled) return;
      const data = res.data?.data as ListResponse;
      setCounts((prev) => ({ ...prev, [otherKategorie]: data.total ?? 0 }));
    }).catch(() => {
      /* silent — Counts sind Komfort, kein Blocker */
    });
    return () => { cancelled = true; };
  }, [filters, kategorie, reloadKey]);

  const groupedOrders = groupOrders(orders);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#E9FF57] rounded">
            <PackageSearch className="w-6 h-6 text-[#1A1A1A]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">Auftragsverfolgung</h1>
            <p className="text-xs text-gray-500">
              {total} Auftrag{total === 1 ? '' : 'e'} insgesamt · Seite {page} von{' '}
              {Math.max(1, totalPages)}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={triggerReload}
          disabled={loading}
          className="p-2 rounded border border-[#DAD9D9] hover:bg-gray-50 disabled:opacity-50"
          title="Aktualisieren"
        >
          <RefreshCw className={`w-4 h-4 text-[#1A1A1A] ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Tabs: Versand-Kategorie */}
      <div className="flex items-center gap-1 border-b border-[#DAD9D9]">
        <button
          type="button"
          onClick={() => handleKategorieChange('paket')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            kategorie === 'paket'
              ? 'border-[#1A1A1A] text-[#1A1A1A]'
              : 'border-transparent text-gray-500 hover:text-[#1A1A1A]'
          }`}
        >
          DHL / GLS
          <span className="ml-2 text-xs text-gray-500">({counts.paket})</span>
        </button>
        <button
          type="button"
          onClick={() => handleKategorieChange('spedition')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            kategorie === 'spedition'
              ? 'border-[#1A1A1A] text-[#1A1A1A]'
              : 'border-transparent text-gray-500 hover:text-[#1A1A1A]'
          }`}
        >
          Spedition
          <span className="ml-2 text-xs text-gray-500">({counts.spedition})</span>
        </button>
      </div>

      {/* Filter */}
      <FilterBar filters={filters} onChange={handleFilterChange} />

      {/* Fehler */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-800 border border-red-200 rounded text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading / Empty / Karten-Grid */}
      {loading && orders.length === 0 ? (
        <div className="text-sm text-gray-500 italic text-center py-12">Lade Auftraege…</div>
      ) : !loading && orders.length === 0 ? (
        <div className="text-sm text-gray-500 italic text-center py-12 border border-dashed border-[#DAD9D9] rounded">
          Keine Auftraege mit diesen Filtern.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {groupedOrders.map(group => (
            <OrderCard key={group.main.id} group={group} kategorie={kategorie} onSelect={setSelectedOrderId} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-4">
          <button
            type="button"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm border border-[#DAD9D9] rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" /> Vorige
          </button>
          <span className="text-sm text-[#1A1A1A]">
            Seite {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm border border-[#DAD9D9] rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Naechste <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Detail-Drawer (on demand) */}
      {selectedOrderId !== null && (
        <OrderDetailDrawer
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onChanged={triggerReload}
        />
      )}
    </div>
  );
}
